import { AuthenticationMethod } from "api/auth";

import { IOrganization } from "models/User";

/**
 * Determines if an organization uses SAML2 as the only authentication method.
 * When SAML2 is the sole method, users cannot use password-based authentication.
 *
 * @param authenticationMethods - The authentication methods string or array from the organization.
 *                                 Can be a comma-separated string ("saml2") or an array (["saml2"])
 * @returns true if SAML2 is the only authentication method
 */
export function isSaml2Only(authenticationMethods?: string | string[]): boolean {
  if (!authenticationMethods) {
    return false;
  }

  // Handle array case
  if (Array.isArray(authenticationMethods)) {
    return (
      authenticationMethods.length === 1 &&
      authenticationMethods[0]?.toLowerCase().trim() === AuthenticationMethod.Saml2
    );
  }

  // Handle string case (could be comma-separated)
  const methods = authenticationMethods
    .split(",")
    .map((m) => m.toLowerCase().trim())
    .filter((m) => m.length > 0);

  return methods.length === 1 && methods[0] === AuthenticationMethod.Saml2;
}

/**
 * Determines if an organization uses SAML2 as the only authentication method.
 *
 * @param organization - The organization object
 * @returns true if SAML2 is the only authentication method
 */
export function isOrganizationSaml2Only(organization?: IOrganization): boolean {
  if (!organization?.authenticationMethods) {
    return false;
  }
  return isSaml2Only(organization.authenticationMethods);
}
